import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminSessionsNav from './AdminSessionsNav';
import useAdminAutoRefresh from './useAdminAutoRefresh';
import './AdminUsers.css';
import './AdminSessions.css';

const AUTO_REFRESH_INTERVAL_MS = 15000;

function formatDateTime(value) {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return 'Not available';
  }

  return date.toLocaleString([], {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return 'Not tracked';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }

  return `${remainingSeconds}s`;
}

function statusClassName(status) {
  switch (status) {
    case 'failed':
      return 'cq-admin-badge cq-admin-badge--disabled';
    case 'degraded':
    case 'stale':
      return 'cq-admin-badge cq-admin-badge--warn';
    case 'completed':
      return 'cq-admin-badge cq-admin-badge--neutral';
    default:
      return 'cq-admin-badge cq-admin-badge--active';
  }
}

const AdminSessionsPage = () => {
  const [filters, setFilters] = useState({
    status: '',
    range: '30d',
    page: 1,
  });
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    error: '',
    sessions: [],
    pagination: {
      page: 1,
      total_pages: 1,
      total_count: 0,
    },
    lastUpdatedAt: null,
  });
  const { refreshNonce, triggerRefresh } = useAdminAutoRefresh({ intervalMs: AUTO_REFRESH_INTERVAL_MS });

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const controller = new AbortController();

    const loadSessions = async ({ background = false } = {}) => {
      setState((current) => ({
        ...current,
        loading: background ? current.loading : true,
        refreshing: background,
        error: '',
      }));

      try {
        const response = await fetch('/.netlify/functions/admin-get-sessions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: filters.status,
            range: filters.range,
            page: filters.page,
            page_size: 25,
          }),
          signal: controller.signal,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load sessions.');
        }

        setState({
          loading: false,
          refreshing: false,
          error: '',
          sessions: data.sessions || [],
          pagination: data.pagination || {
            page: 1,
            total_pages: 1,
            total_count: 0,
          },
          lastUpdatedAt: new Date().toISOString(),
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setState({
          loading: false,
          refreshing: false,
          error: error.message || 'Unable to load sessions.',
          sessions: [],
          pagination: {
            page: 1,
            total_pages: 1,
            total_count: 0,
          },
          lastUpdatedAt: null,
        });
      }
    };

    loadSessions();

    return () => {
      controller.abort();
    };
  }, [filters, refreshNonce]);

  return (
    <section className="cq-admin-page">
      <header className="cq-admin-page__header">
        <div>
          <p className="cq-admin-page__eyebrow">Session Monitoring</p>
          <h2 className="cq-admin-page__title">Sessions</h2>
          <p className="cq-admin-page__copy">Read-only visibility into game session health, completion, and failure context.</p>
        </div>
        <div className="cq-admin-page__actions">
          <div className="cq-admin-page__actions-row">
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.loading || state.refreshing}
              onClick={triggerRefresh}
            >
              Refresh
            </button>
          </div>
          <p className="cq-admin-page__meta">
            {state.refreshing ? 'Refreshing...' : `Last updated ${formatDateTime(state.lastUpdatedAt)}`}
          </p>
        </div>
      </header>

      <AdminSessionsNav />

      <section className="cq-admin-panel cq-admin-users-toolbar">
        <label className="cq-admin-field">
          <span>Status</span>
          <select
            className="cq-admin-input"
            value={filters.status}
            onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value, page: 1 }))}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="starting">Starting</option>
            <option value="started">Started</option>
            <option value="failed">Failed</option>
            <option value="completed">Completed</option>
          </select>
        </label>

        <label className="cq-admin-field">
          <span>Time Range</span>
          <select
            className="cq-admin-input"
            value={filters.range}
            onChange={(event) => setFilters((current) => ({ ...current, range: event.target.value, page: 1 }))}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </label>

        <div className="cq-admin-sessions-toolbar-meta">
          <p className="cq-admin-users-count">{state.pagination.total_count} session{state.pagination.total_count === 1 ? '' : 's'}</p>
        </div>
      </section>

      {state.loading ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">Loading sessions...</p></div> : null}
      {!state.loading && state.error ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">{state.error}</p></div> : null}
      {!state.loading && !state.error && !state.sessions.length ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">No sessions found.</p></div> : null}

      {!state.loading && !state.error && state.sessions.length ? (
        <>
          <section className="cq-admin-panel">
            <div className="cq-admin-table-wrap">
              <table className="cq-admin-table">
                <thead>
                  <tr>
                    <th>Session ID</th>
                    <th>Game Pack</th>
                    <th>Game Mode</th>
                    <th>Host</th>
                    <th>Status</th>
                    <th>Heartbeat</th>
                    <th>Players</th>
                    <th>Started At</th>
                    <th>Duration</th>
                    <th>Error Count</th>
                  </tr>
                </thead>
                <tbody>
                  {state.sessions.map((session) => (
                    <tr key={session.id}>
                      <td className="cq-admin-cell-id">
                        <Link className="cq-admin-card-link" to={`/admin/sessions/${session.id}`}>{session.id}</Link>
                      </td>
                      <td>{session.game_pack?.name || 'Unknown'}</td>
                      <td>{session.game_mode?.name || 'Unknown'}</td>
                      <td>{session.host?.name || 'Unknown'}</td>
                      <td><span className={statusClassName(session.operational_status)}>{session.operational_status}</span></td>
                      <td>
                        {session.stale_seconds
                          ? `Stale ${formatDuration(session.stale_seconds)}`
                          : (session.last_heartbeat_status || 'Not tracked')}
                      </td>
                      <td>{session.player_count ?? 'Not tracked'}</td>
                      <td>{formatDateTime(session.started_at || session.created_at)}</td>
                      <td>{formatDuration(session.duration_seconds)}</td>
                      <td>{session.error_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="cq-admin-session-pagination">
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.pagination.page <= 1}
              onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}
            >
              Previous
            </button>
            <span>Page {state.pagination.page} of {state.pagination.total_pages}</span>
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.pagination.page >= state.pagination.total_pages}
              onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
            >
              Next
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
};

export default AdminSessionsPage;
